import React, { useEffect, useRef } from "react"

import { Box, HStack, Icon, Menu, MenuItem, MenuItemLabel, Pressable } from "wealthmode-frontend/gluestack/ui"
import { MenuIcon, MonitorIcon, MoonIcon, SunIcon } from "wealthmode-frontend/icons"

import { AppHeaderDropdown } from "@/components/header/index"
import { APP_HEADER_HEIGHT_CLASS, useColorMode, useSidebar } from "@/context"

import AppBreadcrumb from "./AppBreadcrumb"

const AppHeader = () => {
  const headerRef = useRef<React.ComponentRef<typeof Box>>(null)
  const { colorMode, setColorMode } = useColorMode()
  const { toggleSidebar } = useSidebar()

  useEffect(() => {
    const handleScroll = () => {
      // react-native-web hands back the underlying DOM node here, but types it as a View.
      const node = headerRef.current as unknown as HTMLElement | null
      if (node) {
        const hasShadow = document.documentElement.scrollTop > 0
        node.style.boxShadow = hasShadow ? "0 1px 3px rgba(0,0,0,0.3)" : "none"
      }
    }
    document.addEventListener("scroll", handleScroll)
    return () => document.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <Box
      className={`sticky top-0 z-50 shrink-0 ${APP_HEADER_HEIGHT_CLASS} bg-background-secondary border-b border-border-medium px-4`}
      ref={headerRef}
    >
      <HStack className="h-full items-center justify-between">
        <HStack className="items-center gap-4">
          <Pressable
            onPress={toggleSidebar}
            className="p-2 rounded hover:bg-background-tertiary"
          >
            <Icon
              as={MenuIcon}
              className="text-foreground-tertiary h-12 w-12"
            />
          </Pressable>
          <Box className="hidden md:flex">
            <AppBreadcrumb />
          </Box>
        </HStack>
        <HStack className="items-center gap-4">
          <Box className="w-px h-6 bg-border-dark" />
          <Menu
            placement="bottom right"
            offset={8}
            className="bg-background-secondary border-border-dark shadow-lg"
            trigger={(triggerProps: any) => (
              <Pressable
                {...triggerProps}
                className="p-2 rounded hover:bg-background-tertiary"
              >
                {colorMode === "dark" ? (
                  <Icon
                    as={MoonIcon}
                    className="text-foreground-tertiary h-10 w-10"
                  />
                ) : colorMode === "system" ? (
                  <Icon
                    as={MonitorIcon}
                    className="text-foreground-tertiary h-10 w-10"
                  />
                ) : (
                  <Icon
                    as={SunIcon}
                    className="text-foreground-tertiary h-10 w-10"
                  />
                )}
              </Pressable>
            )}
          >
            <MenuItem
              className="data-[hover=true]:bg-background-tertiary data-[active=true]:bg-background-tertiary"
              onPress={() => setColorMode("light")}
            >
              <HStack className="items-center gap-2">
                <Icon
                  as={SunIcon}
                  className="text-foreground-tertiary h-9 w-9"
                />
                <MenuItemLabel className="text-foreground-primary">Light</MenuItemLabel>
              </HStack>
            </MenuItem>
            <MenuItem
              className="data-[hover=true]:bg-background-tertiary data-[active=true]:bg-background-tertiary"
              onPress={() => setColorMode("dark")}
            >
              <HStack className="items-center gap-2">
                <Icon
                  as={MoonIcon}
                  className="text-foreground-tertiary h-9 w-9"
                />
                <MenuItemLabel className="text-foreground-primary">Dark</MenuItemLabel>
              </HStack>
            </MenuItem>
            <MenuItem
              className="data-[hover=true]:bg-background-tertiary data-[active=true]:bg-background-tertiary"
              onPress={() => setColorMode("system")}
            >
              <HStack className="items-center gap-2">
                <Icon
                  as={MonitorIcon}
                  className="text-foreground-tertiary h-9 w-9"
                />
                <MenuItemLabel className="text-foreground-primary">Auto</MenuItemLabel>
              </HStack>
            </MenuItem>
          </Menu>
          <Box className="w-px h-6 bg-border-dark" />
          <AppHeaderDropdown />
        </HStack>
      </HStack>
    </Box>
  )
}

export default AppHeader
